import * as THREE from 'three';
import { scene } from './scene';
/* ============================================================
   投射物與拾取物的實例化渲染（M8-d 第二段）
   ============================================================

   雜魚實例化之後 draw call 從 1610 降到 624，但**還是隨敵人數成長** ——
   剩下的是這些池子：碎片 340、經驗球 320、子彈 260、敵彈 220。
   每一個活著的物件都是一個 draw call，戰鬥愈激烈就愈多。

   它們比敵人好處理：單一 mesh、共用材質、沒有階層。

   **不改任何呼叫端**：池子裡的 mesh 建立時就設成 `visible = false`，
   所以既有的 `scene.add(o.m)` / `scene.remove(o.m)` 全部變成無害的空操作
   （three.js 完全跳過看不見的物件），而位置、旋轉、縮放照舊由原本的
   程式碼寫進 `o.m`，我們只是每一幀把它的矩陣複製進 InstancedMesh。

   碎片的顏色是每一片不同的（依敵人配色），走 `instanceColor`。 */

type Reg = {
  pool: any, im: THREE.InstancedMesh,
  pick: ((o: any) => boolean) | null,
  tint: boolean, mul: THREE.Matrix4 | null
};

var regs: Reg[] = [];
var _m = new THREE.Matrix4();

/** 把一個「單一 mesh」的池子接上實例化渲染。
    `pick` 可以只挑池子裡的一部分（例如經驗球與補血球材質不同，各註冊一次）。
    `scale` 會在池子物件自己的矩陣**之後**再乘一次縮放 —— 同一個池子要畫
    兩層（實心彈頭 + 比它大好幾倍的曳光）時，兩層的尺寸關係就寫在這裡，
    而不必去改呼叫端寫進 `o.m.scale` 的值。 */
export function registerPool(pool, geo, mat, opt?: { pick?: (o: any) => boolean, tint?: boolean, scale?: number[] }) {
  opt = opt || {};
  var im = new THREE.InstancedMesh(geo, mat, pool.items.length);
  im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  im.frustumCulled = false;
  im.count = 0;
  if (opt.tint) {
    im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(pool.items.length * 3), 3);
    im.instanceColor.setUsage(THREE.DynamicDrawUsage);
  }
  scene.add(im);
  var mul: THREE.Matrix4 | null = null;
  if (opt.scale) mul = new THREE.Matrix4().makeScale(opt.scale[0], opt.scale[1], opt.scale[2]);
  regs.push({ pool: pool, im: im, pick: opt.pick || null, tint: !!opt.tint, mul: mul });
  /* 讓池子裡的實體 mesh 永遠不畫。既有的 scene.add/remove 因此變成
     無害的空操作，一個呼叫端都不用改。 */
  for (var i = 0; i < pool.items.length; i++) pool.items[i].m.visible = false;
  return im;
}

/** 每一幀呼叫一次（在所有位置都更新完之後）。 */
export function syncFxInstances() {
  for (var r = 0; r < regs.length; r++) {
    var reg = regs[r], items = reg.pool.items, n = 0;
    for (var i = 0; i < items.length; i++) {
      var o = items[i];
      if (!o.alive) continue;
      if (reg.pick && !reg.pick(o)) continue;
      o.m.updateMatrixWorld(true);
      if (reg.mul) { _m.multiplyMatrices(o.m.matrixWorld, reg.mul); reg.im.setMatrixAt(n, _m); }
      else reg.im.setMatrixAt(n, o.m.matrixWorld);
      if (reg.tint) {
        var c = o.m.material.color;
        reg.im.instanceColor!.setXYZ(n, c.r, c.g, c.b);
      }
      n++;
      if (n >= items.length) break;
    }
    reg.im.count = n;
    reg.im.instanceMatrix.needsUpdate = true;
    if (reg.tint) reg.im.instanceColor!.needsUpdate = true;
  }
}
